/**
 * Service Generation Executor
 *
 * Handles the 'service-generation' and 'pipeline-service-gen' node execution.
 * Calls the ModernizeIT API Layer /servicegeneration endpoint.
 *
 * API Flow:
 * 1. POST /servicegeneration → starts generation job, returns job_id
 * 2. Poll GET /servicegeneration/{job_id} for status
 * 3. GET /servicegeneration/{job_id}/results → retrieve full results
 *
 * This node takes Code Analysis output (monolithic Java with real business logic)
 * and generates proper Spring @Service classes with:
 * - Real method bodies (NO stubs except external CALLs)
 * - ERD-based repository injection
 * - Full COBOL traceability mapping
 * - AI-driven method grouping (from Code Refactor recommendations)
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the service generation node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing service generation...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // Build the request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id, // REQUIRED 2026-01-14: For AWS credentials lookup
    // Code Analysis job ID - required to find the Java monolith
    code_analysis_job_id: config.code_analysis_job_id || context.jobs?.code_analysis_job_id || '',
    // Define Services job ID - optional, for HITL-approved service boundaries
    define_services_job_id: config.define_services_job_id || context.jobs?.define_services_job_id || '',
    // AI Naming job ID - optional, for semantic naming (2026-01-18)
    ai_naming_job_id: context.jobs?.ai_naming_job_id,
    // Program name can be specified in config or auto-detected from context
    program_name: config.program_name || context.program_name || null,
    // Base package for generated services
    base_package: config.base_package || 'com.modernizeit.generated',
    // Include all repositories from ERD (default true per user requirement)
    include_all_repositories: config.include_all_repositories !== false,
    // Include all fields from monolith (default true per user requirement)
    include_all_fields: config.include_all_fields !== false,
    // Strict validation - fail if any empty methods (default true)
    strict_validation: config.strict_validation !== false,
    // Target Profile ID — selected in node config panel
    target_profile_id: config.target_profile_id || null,
  }

  let jobResponse
  let jobId

  // Phase 1: Start the generation job
  onProgress(5, 'Submitting service generation job...')

  try {
    const response = await fetch(`${apiBaseUrl}/servicegeneration`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Service generation failed to start')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  // PATCH 3: resolve job IDs — multi-job fan-out or single job
  const jobIds = (jobResponse.job_ids && jobResponse.job_ids.length > 1)
    ? jobResponse.job_ids
    : [jobId]
  const isMultiJob = jobIds.length > 1

  if (isMultiJob) {
    onProgress(10, `Fan-out: ${jobIds.length} jobs started (one per service)`)
  } else {
    onProgress(10, `Job started: ${jobId}`)
  }

  // Check if job completed immediately (synchronous execution)
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, 'Service generation complete')
  } else if (isMultiJob) {
    // Poll all fan-out jobs in parallel — aggregate progress, collect all results
    onProgress(15, `Polling ${jobIds.length} service generation jobs…`)

    const perJobProgress = new Array(jobIds.length).fill(0)
    const allCompletedJobs = new Array(jobIds.length).fill(null)

    await Promise.all(jobIds.map(async (jid, idx) => {
      const completed = await pollJobUntilComplete(jid, apiBaseUrl, {
        maxWaitSeconds: 600,
        pollInterval: 2000,
        onProgress: (pct, msg) => {
          perJobProgress[idx] = pct
          // Aggregate: average across all jobs, mapped to 15-95%
          const avg = perJobProgress.reduce((s, p) => s + p, 0) / jobIds.length
          const mapped = 15 + (avg * 0.80)
          onProgress(mapped, `Job ${idx + 1}/${jobIds.length}: ${msg}`)
        },
        signal,
      })
      allCompletedJobs[idx] = completed
    }))

    // Merge first completed job's data into jobResponse for formatResult
    Object.assign(jobResponse, allCompletedJobs[0] || {})
    jobResponse._all_job_results = allCompletedJobs
    jobResponse._fan_out_job_ids = jobIds
    // Carry service_names parallel array from initial POST response (if present)
    jobResponse._fan_out_service_names = jobResponse.service_names || null
  } else {
    // Worker mode: Poll single job for completion
    onProgress(15, 'Generating Spring services...')
    const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 2000,
      onProgress: (progress, message) => {
        // Map polling progress from 15-95%
        const mappedProgress = 15 + (progress * 0.80)
        onProgress(mappedProgress, message)
      },
      signal
    })

    // Merge completed job data
    Object.assign(jobResponse, completedJob)
  }

  onProgress(100, 'Service generation complete')

  // Return formatted result with job data
  return formatResult(jobId, jobResponse)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data) {
  // PATCH 3: carry fan-out job IDs through so the view can display per-job info
  const fanOutJobIds = data._fan_out_job_ids || null
  const allJobResults = data._all_job_results || null
  const fanOutServiceNames = data._fan_out_service_names || null

  // RC-UI-A: The job poller returns the raw DB record from GET /jobs/{job_id}.
  // Worker-mode jobs store stats inside output_data.summary (nested), not at the
  // root of the job record.  Read from output_data as a fallback so the UI gets
  // real method/service counts instead of zeros.
  const od    = data.output_data || {}
  const odSum = od.summary || {}
  const dSum  = data.summary || {}

  // Build a deduplicated service→jobId map for download buttons.
  // The API creates one job per COBOL program, but multiple programs can belong to
  // the same service (e.g. 4 programs → 1 service).  We keep only the FIRST job ID
  // seen for each distinct service name so the UI renders one button per service.
  let serviceJobMap = null
  if (fanOutJobIds && fanOutServiceNames && fanOutServiceNames.length === fanOutJobIds.length) {
    serviceJobMap = {}
    fanOutJobIds.forEach((jid, i) => {
      const svcName = fanOutServiceNames[i]
      if (svcName && !(svcName in serviceJobMap)) {
        serviceJobMap[svcName] = jid
      }
    })
  }

  return {
    // Job tracking — single ID always present; job_ids populated for fan-out
    jobs: {
      service_generation_job_id: jobId,
      ...(fanOutJobIds ? { service_generation_job_ids: fanOutJobIds } : {}),
      // Deduplicated service→jobId map (one entry per distinct service name).
      // View uses this instead of service_generation_job_ids for download buttons.
      ...(serviceJobMap ? { service_job_map: serviceJobMap } : {}),
    },
    // Per-job completed results (fan-out only, null for single-job)
    all_job_results: allJobResults,

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Execution metrics
    duration_ms: data.duration_ms || od.duration_ms,

    // Summary statistics — RC-UI-A (fix): always normalize to {total_methods, total_services, …}
    // key names regardless of source.
    //
    // The previous `data.summary || od.summary || {...fallback}` short-circuit caused a
    // truth mismatch: worker-mode jobs store stats in output_data.summary with DIFFERENT
    // key names (method_count, service_count) than the UI expects (total_methods,
    // total_services).  When od.summary was truthy it short-circuited to the un-normalized
    // object, so result.summary.total_methods was always undefined → UI showed null / 0.
    //
    // Fix: read all sources exhaustively and always output with the UI-expected keys.
    //   dSum = data.summary (inline-mode already normalised, uses total_* keys)
    //   odSum = output_data.summary (worker-mode, uses method_count / service_count)
    summary: {
      total_methods:       dSum.total_methods      ?? dSum.method_count      ?? data.method_count      ?? data.total_methods      ?? odSum.method_count      ?? od.method_count      ?? 0,
      total_services:      dSum.total_services     ?? dSum.service_count     ?? data.service_count     ?? data.total_services     ?? odSum.service_count     ?? od.service_count     ?? 0,
      methods_per_service: dSum.methods_per_service ?? data.methods_per_service ?? odSum.methods_per_service ?? od.methods_per_service ?? 0,
      validation_passed:   dSum.validation_passed  ?? data.validation_passed ?? odSum.validation_passed ?? od.validation_passed  ?? false,
    },

    // Service-level results (worker-mode never puts these in output_data;
    // the view falls back to contractSummary when this is empty).
    services: data.services || od.services || [],

    // Validation results
    validation: data.validation || {
      passed: data.validation_passed || false,
      checks: data.validation_checks || [],
      errors: data.validation_errors || [],
    },

    // Traceability map (COBOL-to-Java)
    traceability: data.traceability || null,

    // Artifacts produced
    artifacts: {
      services_dir: data.services_dir || null,
      traceability_map: data.traceability_map || null,
      validation_report: data.validation_report || null,
    },

    // Paths for downstream nodes
    artifacts_path: data.artifacts_path || od.artifacts_path,
    output_path: data.output_path || null,

    // Download info
    download_available: data.download_available || false,

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
